import React, { useEffect, useState } from "react";
import { CircleDollarSign, LogOut, Settings, UserRound } from "lucide-react";
import CONFIG from "config/config";
import { authorization } from "module/auth";
import { meRequestInit, meUrl } from "module/api/me";
import { Popover, PopoverTrigger, PopoverContent } from "components/ui/popover";
import { Avatar, AvatarImage, AvatarFallback } from "components/ui/avatar";

interface UserMenuProps {
  clientId?: string;
  /** Kept for API compatibility; the header shows name + email (Figma 3573:127239). */
  companyName?: string;
}

function initialsOf(first: string, last: string, fallback: string): string {
  const initials = `${first.trim().charAt(0)}${last.trim().charAt(0)}`.toUpperCase();
  return initials || fallback.trim().charAt(0).toUpperCase() || "U";
}

// the API sends a silhouette instead of null when there's no photo, so truthiness can't reach the initials fallback (PAD-31716 AC3)
const DEFAULT_PROFILE_PIC_MARKER = "default-profile-pic";

function photoUrlOf(user: { profilePicture?: string; updatedAt?: string } | null | undefined): string | undefined {
  const picture = user?.profilePicture;
  if (!picture || picture.includes(DEFAULT_PROFILE_PIC_MARKER)) return undefined;
  return `${picture}${user?.updatedAt ? `?${user.updatedAt}` : ""}`;
}

// once per JS context: every builder step mounts its own header, and changing your photo means leaving the builder (so a fresh load always follows); cleared on failure to allow a retry
let liveUserRequest: Promise<Record<string, any> | null> | null = null;

function fetchLiveUser(): Promise<Record<string, any> | null> {
  if (liveUserRequest) return liveUserRequest;
  const apiKey = authorization.getAPIKey();
  if (!apiKey) return Promise.resolve(null);
  liveUserRequest = fetch(meUrl(), meRequestInit(apiKey))
    .then((response) => (response.ok ? response.json() : null))
    .then((body) => (body ? body.user || body : null))
    .catch(() => {
      liveUserRequest = null;
      return null;
    });
  return liveUserRequest;
}

// auth storage is a handoff-time snapshot that never refreshes, so a photo uploaded later never shows (PAD-31716 AC4); the avatar is cosmetic so failures just keep the snapshot
function useLiveUser() {
  const [liveUser, setLiveUser] = useState<Record<string, any> | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchLiveUser().then((user) => {
      if (!cancelled && user) setLiveUser(user);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return liveUser;
}

// Account menu for the builder header. Identical to platform-next's HeaderUserMenu
// and Figma 3573:127239: avatar-only trigger; dropdown = user header (name + email)
// -> "Your account" -> Profile/Billing/Settings -> Logout. Built on radix Popover
// (the app's dropdown-menu dep is a legacy 0.1.x pinned by the flyer builder).
// Profile/Billing/Settings are next-owned since PAD-30000/30001/30002 migrated them; only Logout stays on PLATFORM_BASE_URL (legacy owns session teardown).
export const UserMenu: React.FC<UserMenuProps> = ({ clientId }) => {
  const storedUser = authorization.getUser();
  const liveUser = useLiveUser();
  const user = liveUser ?? storedUser;
  const firstName = user?.firstName ?? "";
  const lastName = user?.lastName ?? "";
  const fullName = `${firstName} ${lastName}`.trim() || user?.email || "Account";
  const email = user?.email ?? "";
  const photoUrl = photoUrlOf(user);
  const initials = initialsOf(firstName, lastName, fullName);

  const links = [
    { key: "Profile", label: "Profile", Icon: UserRound, href: `${CONFIG.NEXT_BASE_URL}/dashboard/settings/profile` },
    {
      key: "Billing",
      label: "Billing",
      Icon: CircleDollarSign,
      href: `${CONFIG.NEXT_BASE_URL}/dashboard/${clientId}/billing`,
    },
    {
      key: "Settings",
      label: "Settings",
      Icon: Settings,
      href: `${CONFIG.NEXT_BASE_URL}/dashboard/settings`,
    },
  ];

  const handleLogout = () => {
    authorization.logout();
    window.location.href = `${CONFIG.PLATFORM_BASE_URL}/logout`;
  };

  const itemClass =
    "flex w-full items-center gap-2 rounded-[4px] px-2 py-1.5 text-sm text-foreground no-underline outline-none hover:bg-accent focus:bg-accent";
  const iconClass = "h-4 w-4 shrink-0 text-muted-foreground";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Open user menu"
          data-cypress-id="builderUserMenu"
          cypress-id="builderUserMenu"
          className="rounded-full outline-none transition-shadow hover:ring-2 hover:ring-border focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:ring-2 data-[state=open]:ring-border"
        >
          <Avatar className="h-8 w-8">
            {photoUrl && <AvatarImage src={photoUrl} alt="" />}
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" sideOffset={8} className="cb-v2-root w-56 overflow-hidden p-0">
        {/* User header — 40px avatar + name + email */}
        <div className="flex items-center gap-2 p-3">
          <Avatar>
            {photoUrl && <AvatarImage src={photoUrl} alt="" />}
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col py-0.5">
            <span className="truncate text-sm font-medium leading-5 text-foreground">{fullName}</span>
            {email && <span className="truncate text-xs leading-4 text-muted-foreground">{email}</span>}
          </div>
        </div>

        <div className="h-px w-full bg-border" />

        <div className="p-1">
          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Your account</div>
          {links.map(({ key, label, Icon, href }) => (
            <a
              key={key}
              href={href}
              className={itemClass}
              data-cypress-id={`builderUserMenu${key}`}
              cypress-id={`builderUserMenu${key}`}
            >
              <Icon className={iconClass} />
              <span>{label}</span>
            </a>
          ))}
        </div>

        <div className="h-px w-full bg-border" />

        <div className="p-1">
          <button
            type="button"
            onClick={handleLogout}
            className={itemClass}
            data-cypress-id="builderUserMenuLogout"
            cypress-id="builderUserMenuLogout"
          >
            <LogOut className={iconClass} />
            <span>Logout</span>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
